import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { secretsStore, type SecretCreateRequest } from '~/lib/stores/secrets';
import { projectStore } from '~/lib/stores/project';
import { recheckMissingSecrets } from '~/lib/stores/supabase/find-secrets';

export const useGetSecrets = () => {
  const projectId = projectStore.projectId.get();

  return useQuery({
    queryKey: ['secrets', projectId],
    queryFn: async () => {
      await secretsStore.fetchSecrets();

      // Return the current secrets from the store after fetching
      return secretsStore.secrets.get();
    },
    enabled: !!projectId,
    retry: false,
  });
};

export const useCreateSecrets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { secrets: SecretCreateRequest[] }) => {
      return secretsStore.createSecrets(data.secrets);
    },
    onSuccess: async () => {
      toast.success(`Secrets created successfully.`);
      queryClient.invalidateQueries({
        queryKey: ['secrets'],
      });

      // Re-check for missing secrets to potentially dismiss the missing secrets toast
      try {
        await recheckMissingSecrets();
      } catch (error) {
        // Don't fail the success callback if secrets checking fails
        console.warn('Failed to re-check missing secrets:', error);
      }
    },
    onError: (error: { message?: string }) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'There was a problem with your request.';
      toast.error(message);
    },
  });
};

export const useDeleteSecrets = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { secretNames: string[] }) => {
      return await secretsStore.deleteSecrets(data.secretNames);
    },
    onSuccess: async () => {
      toast.success(`Secrets deleted successfully.`);

      queryClient.invalidateQueries({
        queryKey: ['secrets'],
      });

      // Re-check for missing secrets to potentially show missing secrets toast if deletion caused missing secrets
      try {
        await recheckMissingSecrets();
      } catch (error) {
        // Don't fail the success callback if secrets checking fails
        console.warn('Failed to re-check missing secrets:', error);
      }
    },
    onError: (error: { message?: string }) => {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'string'
            ? error
            : 'There was a problem with your request.';
      toast.error(message);
    },
  });
};
